import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea, VoiceToTextInput } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertTriangle, Trash2, Send, CheckCircle2, Eye, Code2 } from "lucide-react";
import { sanitizeEmailHtml } from "@/lib/sanitize-html";

export interface FailedDraft {
  id: string;
  workspaceId: string;
  userId: string;
  channel: "email" | "sms";
  endpoint: string | null;
  method: string | null;
  subject: string | null;
  body: string;
  recipientEmail: string | null;
  recipientPhone: string | null;
  cc: string | null;
  payload: Record<string, unknown> | null;
  blockCode: string | null;
  blockMessage: string | null;
  blockTokens: string[] | null;
  contextLabel: string | null;
  createdAt: string;
  // Outbox extensions (Task: never-silent-fallback).
  status?: "pending" | "sent" | "cancelled" | "failed_permanent" | null;
  attemptCount?: number | null;
  lastAttemptAt?: string | null;
  nextAttemptAt?: string | null;
  lastError?: string | null;
  sourceType?: string | null;
  sourceId?: string | null;
  needsReconnect?: boolean | null;
}

interface Props {
  draftId: string;
  onSent?: () => void;
  onDiscarded?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
}

export function EmailDraftEditor({ draftId, onSent, onDiscarded, onCancel, showCancel = true }: Props) {
  const { toast } = useToast();
  const { data: draft, isLoading, error } = useQuery<FailedDraft>({
    queryKey: ["/api/email-drafts", draftId],
    enabled: !!draftId,
  });

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipient, setRecipient] = useState("");
  const [cc, setCc] = useState("");
  const [isEditingBody, setIsEditingBody] = useState(false);

  useEffect(() => {
    if (draft) {
      setSubject(draft.subject ?? "");
      setBody(draft.body ?? "");
      setRecipient(draft.recipientEmail ?? draft.recipientPhone ?? "");
      setCc(draft.cc ?? "");
      const hasTokens = (draft.blockTokens ?? []).length > 0;
      setIsEditingBody(hasTokens);
    }
  }, [draft]);

  const resend = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error("Draft not loaded");
      // Always go through the unified resend endpoint — works for every
      // saved draft regardless of whether the original send had a
      // recoverable HTTP endpoint (automated/scheduler-originated drafts
      // do not, so the old "replay original endpoint" approach failed for
      // exactly the cases users are most likely to need to fix).
      return apiRequest("POST", `/api/email-drafts/${draftId}/resend`, {
        to: recipient,
        cc: draft.channel === "email" ? cc : undefined,
        subject,
        body,
      });
    },
    onSuccess: async () => {
      try { await apiRequest("DELETE", `/api/email-drafts/${draftId}`); } catch { /* ignore */ }
      queryClient.invalidateQueries({ queryKey: ["/api/email-drafts"] });
      toast({ title: "Sent", description: "Draft was sent successfully." });
      onSent?.();
    },
    onError: async (err: any) => {
      let detail = err?.message || "Send failed";
      try {
        const j = err?.responseBody ? JSON.parse(err.responseBody) : null;
        if (j?.message) detail = j.message;
        if (Array.isArray(j?.tokens) && j.tokens.length) {
          detail += ` (unfilled: ${j.tokens.join(", ")})`;
        }
      } catch { /* ignore */ }
      toast({ title: "Still blocked", description: detail, variant: "destructive" });
    },
  });

  const remove = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/email-drafts/${draftId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-drafts"] });
      toast({ title: "Draft discarded" });
      onDiscarded?.();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="status-loading-draft">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div data-testid="status-draft-not-found">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>This draft could not be loaded. It may have been deleted or already sent.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const isSms = draft.channel === "sms";
  const tokens = draft.blockTokens || [];

  return (
    <div className="space-y-4" data-testid="component-email-draft-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold" data-testid="text-draft-title">
            {draft.status === "sent" ? `Sent ${isSms ? "SMS" : "email"}` : `Fix & resend ${isSms ? "SMS" : "email"} draft`}
          </h2>
          <p className="text-sm text-muted-foreground mt-1" data-testid="text-context-label">
            {draft.contextLabel ? `Saved from: ${draft.contextLabel}` : "Saved blocked send"}
            {" · "}
            {new Date(draft.createdAt).toLocaleString()}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => remove.mutate()} disabled={remove.isPending} data-testid="button-discard-draft">
          <Trash2 className="w-4 h-4 mr-1" /> Discard
        </Button>
      </div>

      {draft.status === "sent" ? (
        <Alert className="border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-300" data-testid="alert-sent-success">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription>
            <div className="font-medium text-green-800 dark:text-green-300">Successfully sent</div>
            <div className="mt-0.5 text-sm text-green-700 dark:text-green-400">
              Blocked at {new Date(draft.createdAt).toLocaleString()}
              {draft.lastAttemptAt ? ` · Sent at ${new Date(draft.lastAttemptAt).toLocaleString()}` : ""}
            </div>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive" data-testid="alert-block-reason">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-medium">Send was blocked: {draft.blockMessage}</div>
            {tokens.length > 0 && (
              <div className="mt-1 text-sm" data-testid="text-unfilled-tokens">
                Unfilled merge tokens: <code>{tokens.join(", ")}</code>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{isSms ? "SMS message" : "Email"}</CardTitle>
          <CardDescription>
            Replace any remaining <code>{"{{token}}"}</code> placeholders before resending.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`recipient-${draftId}`}>{isSms ? "To (phone)" : "To (email)"}</Label>
            <Input id={`recipient-${draftId}`} value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="input-recipient" />
          </div>
          {!isSms && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor={`cc-${draftId}`}>Cc</Label>
                <Input id={`cc-${draftId}`} value={cc} onChange={(e) => setCc(e.target.value)} placeholder="optional" data-testid="input-cc" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`subject-${draftId}`}>Subject</Label>
                <VoiceToTextInput id={`subject-${draftId}`} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="input-subject" />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor={`body-${draftId}`}>{isSms ? "Message" : "Body"}</Label>
              {!isSms && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground"
                  onClick={() => setIsEditingBody((v) => !v)}
                  data-testid="button-toggle-body-mode"
                >
                  {isEditingBody ? (
                    <><Eye className="w-3.5 h-3.5 mr-1" /> Preview</>
                  ) : (
                    <><Code2 className="w-3.5 h-3.5 mr-1" /> Edit HTML</>
                  )}
                </Button>
              )}
            </div>
            {isSms || isEditingBody ? (
              <VoiceToTextTextarea
                id={`body-${draftId}`}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={isSms ? 6 : 16}
                className="font-mono text-sm"
                data-testid="input-body"
              />
            ) : (
              <div
                className="min-h-[18rem] rounded-lg border bg-white dark:bg-zinc-950 p-4 overflow-y-auto prose prose-sm max-w-none dark:prose-invert [&_a]:text-blue-600 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(body) }}
                data-testid="preview-body"
              />
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            {showCancel && (
              <Button variant="ghost" onClick={() => onCancel?.()} data-testid="button-cancel">Cancel</Button>
            )}
            {draft.status !== "sent" && (
              <Button onClick={() => resend.mutate()} disabled={resend.isPending} data-testid="button-resend">
                {resend.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4 mr-1" /> Resend</>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
